import Day from '@awesomecomponents/day-js';
import { BREAKPOINTS, Button, Checkbox, Dropdown, H2, MessageCard, Modal, P, TextLink, useMedia } from '@manulife/mux';
import { useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { GetFamily } from '../App/ReducerActions/GetFamily';
import ExtendCoverageModalLoadingIndicator from '../LoadingIndicator/ExtendCoverageModalLoadingIndicator';
import { DESTINATIONS, TransferOutWithToken } from '../Shared/ReducerActions/TransferOutWithToken';
import { ButtonWrapper, CheckboxWrapper, ErrorMsg, ValidationAlertWrapper } from '../Shared/SharedStyles';
import { ExtendStudentStatus } from './ReducerActions/ExtendStudentStatus';
import { getExtendCoverageValidationSchema } from './Validations';

const BoldLabel = styled.strong`
    display: block;
    margin-top: 16px;
`;

const DEFAULT_FAMILY_MEMBER = {
    gender: '',
    firstName: '',
    lastName: '',
    // Mux component Date of birth expects object as a default value.
    extensionReason: '',
    dateOfBirth: '',
    familyMemberAccuracyAgreement: false,
};

const ExtendCoverageModal = () => {
    const { isLoadingMemberData, language, isExtendCoverageDataSaving, currentFormDependent, errorUpdate, isCsr } =
        useShallowEqualSelector((state) => ({
            isLoadingMemberData: state.MemberData.isLoadingMemberData,
            language: state.Authentication.language,
            isExtendCoverageDataSaving: state.MemberData.isExtendCoverageDataSaving,
            currentFormDependent: state.MemberData.currentFormDependent,
            errorUpdate: state.Errors.familyMemberUpdate,
            isCsr: state.Authentication.isCsr,
        }));

    const lang = language === 'fr-CA' ? 'fr' : 'en';
    const history = useHistory();
    const dispatch = useDispatch();
    const errorRef = useRef();

    const mobileView = useMedia([`(max-width: ${BREAKPOINTS.TABLET_PORTRAIT}px)`], [true], false);
    const customFieldStyle = mobileView ? { marginTop: '12px', width: '100%' } : { marginTop: '12px' };

    const cancelButton = useAEMContent(FRAGMENT_MP.common, 'cancelButton');
    const closeButton = useAEMContent(FRAGMENT_MP.common, 'closeButton');
    const saveButton = useAEMContent(FRAGMENT_MP.common, 'saveButton');
    const dateOfBirthLabel = useAEMContent(FRAGMENT_MP.common, 'dateOfBirthLabel');
    const childTag = useAEMContent(FRAGMENT_MP.common, 'child');

    const extendStudentButton = useAEMContent(FRAGMENT_MP.familyMember, 'extendStudentButton');
    const fullNameLabel = useAEMContent(FRAGMENT_MP.familyMember, 'fullNameLabel');
    const reasonForExtendingCoverageLabel = useAEMContent(FRAGMENT_MP.familyMember, 'reasonForExtendingCoverageLabel');
    const fullTimeStudentLabel = useAEMContent(FRAGMENT_MP.familyMember, 'fullTimeStudentLabel');
    const disabledLabel = useAEMContent(FRAGMENT_MP.familyMember, 'disabledLabel');
    const disabledMessage = useAEMContent(FRAGMENT_MP.familyMember, 'disabledMessage');
    const disabledFormText = useAEMContent(FRAGMENT_MP.familyMember, 'disabledFormText');
    const disabledFormPath = useAEMContent(FRAGMENT_MP.familyMember, 'disabledFormPath');
    const sendFileText = useAEMContent(FRAGMENT_MP.familyMember, 'sendFileText');
    const sendDocuments = useAEMContent(FRAGMENT_MP.familyMember, 'sendDocuments');
    const sendFilePath = useAEMContent(FRAGMENT_MP.familyMember, 'sendFilePath');
    const alternativeAddress = useAEMContent(FRAGMENT_MP.familyMember, 'alternativeAddress');
    const extendCoverageReasonPlaceholder = useAEMContent(FRAGMENT_MP.familyMember, 'extendCoverageReasonPlaceholder');
    const relationshipLabel = useAEMContent(FRAGMENT_MP.familyMember, 'relationshipLabel');
    const declarationMessage = useAEMContent(FRAGMENT_MP.familyMember, 'declarationMessage', { parseHTML: true });

    const validationMessages = {
        errorSummaryTitle: useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryTitle'),
        errorSummaryMessage: useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryMessage'),
        studentCoverageExtensionReasonBlank: useAEMContent(
            FRAGMENT_MP.validationMessages,
            'studentCoverageExtensionReasonBlank'
        ),
        certifyCheckBox: useAEMContent(FRAGMENT_MP.validationMessages, 'certifyCheckBox'),
    };

    const { handleSubmit, values, touched, errors, isValid, isSubmitting, setFieldValue, submitCount } = useFormik({
        initialValues: {
            ...DEFAULT_FAMILY_MEMBER,
        },
        onSubmit: async () => {
            const response = await dispatch(ExtendStudentStatus(values));
            if (response.success) {
                dispatch(GetFamily());
                history.push('/gb/member-profile-2/family');
            }
        },
        validationSchema: Yup.object().shape(getExtendCoverageValidationSchema(validationMessages)),
    });

    /**
     * Build a family member object to prepopulate the extend coverage modal and clear any errors from previous render
     */
    useEffect(() => {
        if (currentFormDependent === undefined) {
            history.push('/gb/member-profile-2/family');
        } else {
            const { personId, firstName, lastName, birthDate, gender } = currentFormDependent;

            setFieldValue('personId', personId, false);
            setFieldValue('firstName', firstName, false);
            setFieldValue('lastName', lastName, false);
            setFieldValue('gender', gender, false);
            setFieldValue('dateOfBirth', new Day(birthDate).format(lang, 'MMM, D, YYYY'), false);
            setFieldValue('familyMemberAccuracyAgreement', false, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFormDependent, setFieldValue, lang]);
    // TODO: Upgrade to useNavigate or redirect from useHistory which is going to be deprecated.
    // See: https://jira.manulife.ca/browse/GBSLTS-104, https://reactrouter.com/en/main/hooks/use-navigate,
    // https://reactrouter.com/en/main/fetch/redirect

    // Scrolls to error messages at the top of the modal on submit.
    useEffect(() => {
        if (!isValid && isSubmitting) {
            errorRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [isValid, isSubmitting]);

    return (
        <Modal aria-label="Extend Coverage" isOpen={true} onClose={() => history.push('/gb/member-profile-2/family')}>
            {isLoadingMemberData && (
                <ExtendCoverageModalLoadingIndicator data-testid="extendCoverageModalLoadingIndicator" />
            )}
            {!isLoadingMemberData && (
                <form onSubmit={handleSubmit} id="extendCoverageModal">
                    <H2>{extendStudentButton}</H2>
                    <ValidationAlertWrapper
                        id="addFamilyMemberErrors"
                        ref={errorRef}
                        aria-hidden={isValid}
                        aria-live="polite"
                        aria-atomic="true"
                        role="alert"
                    >
                        {errorUpdate?.hasError && (
                            <MessageCard
                                variant={MessageCard.VARIANT.PRIMARY_ALERT}
                                titleText={validationMessages.errorSummaryTitle}
                            >
                                Problem: {errorUpdate.message} Code: {errorUpdate.code}
                            </MessageCard>
                        )}

                        {!isValid && submitCount > 0 && (
                            <MessageCard
                                variant={MessageCard.VARIANT.PRIMARY_ALERT}
                                titleText={validationMessages.errorSummaryTitle}
                            >
                                {validationMessages.errorSummaryMessage}
                            </MessageCard>
                        )}
                    </ValidationAlertWrapper>
                    <BoldLabel>{fullNameLabel}</BoldLabel>
                    <div>
                        {values.firstName} {values.lastName}
                    </div>
                    <BoldLabel>{dateOfBirthLabel}</BoldLabel>
                    <div>{values.dateOfBirth}</div>
                    <BoldLabel> {relationshipLabel}</BoldLabel>
                    <div>{childTag}</div>
                    <Dropdown
                        id="extensionReason"
                        label={reasonForExtendingCoverageLabel}
                        customStyle={{ rootStyle: customFieldStyle }}
                        dropdownItems={[
                            {
                                label: fullTimeStudentLabel,
                                value: 'fullTimeStudent',
                            },
                            {
                                label: disabledLabel,
                                value: 'disabled',
                            },
                        ]}
                        placeholder={extendCoverageReasonPlaceholder}
                        onChange={(newValue) => setFieldValue('extensionReason', newValue)}
                        value={values.extensionReason}
                        errors={
                            touched.extensionReason && [
                                {
                                    id: 'extensionReason',
                                    msg: errors.extensionReason,
                                    isError: Boolean(errors.extensionReason),
                                },
                            ]
                        }
                        disabled={isExtendCoverageDataSaving}
                        callouts
                        ariaRequired={true}
                        data-testid="extensionReason"
                    />
                    {values.extensionReason === 'disabled' ? (
                        <>
                            <P>{disabledMessage}</P>
                            <TextLink
                                id="sendFormLink"
                                to="#"
                                as={Link}
                                isExternal
                                onClick={() =>
                                    dispatch(
                                        TransferOutWithToken(DESTINATIONS.planMember, disabledFormPath, '', '_blank')
                                    )
                                }
                            >
                                {disabledFormText}
                            </TextLink>
                            <P>
                                {sendFileText}{' '}
                                <TextLink
                                    id="sendFileLink"
                                    to="#"
                                    as={Link}
                                    isExternal
                                    onClick={() =>
                                        dispatch(
                                            TransferOutWithToken(DESTINATIONS.planMember, sendFilePath, '', '_blank')
                                        )
                                    }
                                >
                                    {sendDocuments}
                                </TextLink>
                            </P>
                            <P>{alternativeAddress}</P>
                        </>
                    ) : (
                        <CheckboxWrapper>
                            <Checkbox
                                id="familyMemberAccuracyAgreement"
                                checked={values.familyMemberAccuracyAgreement}
                                onChange={(newValue) => setFieldValue('familyMemberAccuracyAgreement', newValue)}
                                error={errors.familyMemberAccuracyAgreement && touched.familyMemberAccuracyAgreement}
                                disabled={isExtendCoverageDataSaving}
                                aria-required="true"
                            />
                            <label htmlFor="familyMemberAccuracyAgreement">
                                {declarationMessage}
                                {errors.familyMemberAccuracyAgreement && touched.familyMemberAccuracyAgreement && (
                                    <ErrorMsg
                                        id="accuracyAgreement-error"
                                        role="alert"
                                        aria-atomic="true"
                                        aria-live="polite"
                                    >
                                        {errors.familyMemberAccuracyAgreement}
                                    </ErrorMsg>
                                )}
                            </label>
                        </CheckboxWrapper>
                    )}
                    <ButtonWrapper>
                        {values.extensionReason === 'disabled' ? (
                            <Button
                                id="closeButton"
                                type="button"
                                customStyle={{
                                    buttonStyle: { margin: 'auto' },
                                }}
                                onClick={() => history.push('/gb/member-profile-2/family')}
                            >
                                {closeButton}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    id="cancelExtendCoverageButton"
                                    variant={Button.VARIANT.SECONDARY}
                                    onClick={() => history.push('/gb/member-profile-2/family')}
                                    disabled={isExtendCoverageDataSaving}
                                >
                                    {cancelButton}
                                </Button>
                                <Button
                                    id="updateExtendCoverageButton"
                                    type="submit"
                                    saving={isExtendCoverageDataSaving}
                                    disabled={isExtendCoverageDataSaving || isCsr}
                                >
                                    {saveButton}
                                </Button>
                            </>
                        )}
                    </ButtonWrapper>
                </form>
            )}
        </Modal>
    );
};

export default ExtendCoverageModal;
